import {
  Badge, Button, Card, ErrorState, Field, Input, LoadingState, LogoPicker, Notice, PageHeader, Row, Select, Stack,
  useApi, useI18n, useTenantSettings,
  type SettingResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CatalogueCard } from '../components/CatalogueCard';
import { IdentityCard } from '../components/IdentityCard';

/** How each setting is edited. Anything unlisted falls back to a plain text box. */
const CONTROLS: Record<string, 'boolean' | 'time' | 'number' | 'visibility'> = {
  'daily.review_required': 'boolean',
  'daily.require_check_in': 'boolean',
  'attendance.cutoff_time': 'time',
  'attendance.cutoff_alert_minutes': 'number',
  'attendance.pickup_required': 'boolean',
  'attendance.health_check': 'boolean',
  'photos.default_visibility': 'visibility',
  'photos.retention_months': 'number',
  'notifications.digest_window_minutes': 'number',
  'notifications.closing_time': 'time',
  'notifications.sms_enabled': 'boolean',
  'messages.attachments_enabled': 'boolean',
  'finance.deadline_term': 'number',
  'reminders.send_time': 'time',
  'reminders.payment_overdue_every_days': 'number',
  'reminders.payment_sms_last_steps': 'number',
  'reminders.calendar_event_days': 'number',
  'exports.window_days': 'number',
  'exports.file_days': 'number',
  'retention.daily_care_months': 'number',
  'retention.attendance_months': 'number',
  'retention.finance_months': 'number',
};

/**
 * Settings the API still serves but the v1 console does not offer. v1 bills a yearly total per child (Q3, Q15),
 * so monthly generation and its due day are the engine underneath, off, until a nursery needs them.
 */
const HIDDEN = new Set(['finance.due_day_of_month', 'finance.generation_day']);

/**
 * The tenant's own configuration (§3.6).
 *
 * Every setting shows the value in force, whether or not the nursery has chosen it, and marks the ones
 * still on the shipped default. A screen that showed blanks for unset values would make an owner guess
 * what happens when they leave one alone.
 *
 * These are not cosmetic. The cut-off time decides who counts as absent, the photo default decides who
 * sees a picture of somebody's child, and the digest window decides how often a family's phone buzzes.
 */
export function Settings() {
  const api = useApi();
  const { t, tn, has, formatNumber } = useI18n();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const settings = useQuery({ queryKey: ['admin', 'settings'], queryFn: () => api.admin.settings() });
  const { settings: tenant } = useTenantSettings();

  // The logo lives on the tenant record, not among the keyed settings, so it is its own card and saves at
  // once rather than waiting for the page's Save.
  const logoChanged = () => queryClient.invalidateQueries({ queryKey: ['tenant', 'current'] });

  const save = useMutation({
    mutationFn: () => api.admin.updateSettings(draft),
    onSuccess: async () => {
      setDraft({});
      // The dashboard and every board read these, so their caches go too.
      await queryClient.invalidateQueries();
    },
  });

  if (settings.isPending) return <LoadingState />;
  if (settings.isError) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }

  const valueOf = (setting: SettingResponse) => draft[setting.key] ?? setting.value;

  const review = settings.data.find((setting) => setting.key === 'daily.review_required');
  const reviewSwitchingOff = review !== undefined && review.value.toLowerCase() === 'true'
    && draft[review.key]?.toLowerCase() === 'false';

  const control = (setting: SettingResponse) => {
    const kind = CONTROLS[setting.key];
    const value = valueOf(setting);
    const set = (next: string) => setDraft((current) => ({ ...current, [setting.key]: next }));

    switch (kind) {
      case 'boolean':
        return (
          <label className="dg-checkbox">
            <input
              type="checkbox"
              checked={value.toLowerCase() === 'true'}
              onChange={(event) => set(event.target.checked ? 'True' : 'False')}
            />
            <span>{label(setting.key)}</span>
          </label>
        );
      case 'time':
        return (
          <Field label={label(setting.key)}>
            {(props) => (
              <Input {...props} type="time" value={value} onChange={(event) => set(event.target.value)} />
            )}
          </Field>
        );
      case 'number':
        // The range is the server's, so the screen and the save that checks it cannot disagree.
        return (
          <Field label={label(setting.key)} hint={range(setting)}>
            {(props) => (
              <Input {...props}
                type="number"
                inputMode="numeric"
                min={setting.minimum ?? undefined}
                max={setting.maximum ?? undefined}
                value={value}
                onChange={(event) => set(event.target.value)}
              />
            )}
          </Field>
        );
      case 'visibility':
        return (
          <Field label={label(setting.key)}>
            {(props) => (
              <Select {...props} value={value} onChange={(event) => set(event.target.value)}>
                <option value="SingleChild">{t('photos.visibility.SingleChild')}</option>
                <option value="SharedTagged">{t('photos.visibility.SharedTagged')}</option>
              </Select>
            )}
          </Field>
        );
      default:
        return (
          <Field label={label(setting.key)}>
            {(props) => (
              <Input {...props} value={value} onChange={(event) => set(event.target.value)} />
            )}
          </Field>
        );
    }
  };

  function range(setting: SettingResponse): string | undefined {
    return setting.minimum !== null && setting.maximum !== null
      ? t('settings.range', { min: formatNumber(setting.minimum), max: formatNumber(setting.maximum) })
      : undefined;
  }

  function label(key: string): string {
    return has(`settings.${key}`) ? t(`settings.${key}`) : key;
  }

  return (
    <Stack>
      <PageHeader
        title={t('settings.title')}
        actions={
          <Button
            busy={save.isPending}
            busyLabel={t('action.saving')}
            disabled={Object.keys(draft).length === 0}
            onClick={() => save.mutate()}
          >
            {t('action.save')}
          </Button>
        }
      />

      {tenant && <IdentityCard tenant={tenant} />}

      {tenant && (
        <Card title={t('logo.title')}>
          <LogoPicker
            name={tenant.nameAr}
            logoUrl={tenant.logoUrl}
            hint={t('logo.hint')}
            onSave={async (logo) => { await api.admin.setLogo(logo); await logoChanged(); }}
            onRemove={async () => { await api.admin.removeLogo(); await logoChanged(); }}
          />
        </Card>
      )}

      {save.isError && <ErrorState error={save.error} />}
      {save.isSuccess && (
        <Notice tone="success">
          <Stack>
            <span>{t('settings.saved')}</span>
            {/* Switching review off releases the days waiting for it (C1), which the owner should hear about. */}
            {save.data.draftsPublished > 0 && <span>{tn('settings.draftsPublished', save.data.draftsPublished)}</span>}
            {save.data.draftsHeldByClosedTerms > 0 && (
              <span>{tn('settings.draftsHeld', save.data.draftsHeldByClosedTerms)}</span>
            )}
          </Stack>
        </Notice>
      )}
      {reviewSwitchingOff && <Notice tone="warning">{t('settings.reviewOff.warning')}</Notice>}

      <Card>
        <Stack>
          {settings.data.filter((setting) => !HIDDEN.has(setting.key)).map((setting) => (
            <Row key={setting.key} between>
              <span style={{ flex: 1 }}>{control(setting)}</span>
              {/* Marking the defaults tells an owner which decisions are still ours rather than theirs. */}
              {setting.isDefault && <Badge tone="neutral">{t('settings.default')}</Badge>}
            </Row>
          ))}
        </Stack>
      </Card>

      <CatalogueCard />
    </Stack>
  );
}
